import React from "react";
import { Box, createStyles, Loader, LoadingOverlay } from "@mantine/core";
import { tokens } from "@blurb/codex-react";
import { Body } from "../../../components/Typography";
import { formatErrorDetail } from "../../../utils/query.util";

const useStyles = createStyles({
  errorBox: {
    padding: tokens.spacing[4],
    display: "flex",
    flexDirection: "column",
    gap: tokens.spacing[2],
  },
  subtleText: {
    color: tokens.color.semantic.text.subtle,
  },
});

interface OrderTabContentProps<T> {
  data: T[];
  isLoading: boolean;
  error: unknown;
  emptyMessage: string;
  loadedTestId?: string;
  children: (data: T[]) => React.ReactNode;
}

export function OrderTabContent<T>({
  data,
  isLoading,
  error,
  emptyMessage,
  loadedTestId,
  children,
}: OrderTabContentProps<T>) {
  const { classes } = useStyles();

  if (isLoading) {
    return (
      <Box sx={{ position: "relative", minHeight: 200 }}>
        <LoadingOverlay loader={<Loader color="brand" size="xl" />} visible={true} />
      </Box>
    );
  }

  if (error) {
    const errorDetail = formatErrorDetail(error);
    return (
      <Box className={classes.errorBox}>
        <Body>Failed to load data.</Body>
        {errorDetail && (
          <Body size="sm" className={classes.subtleText}>
            {errorDetail}
          </Body>
        )}
      </Box>
    );
  }

  if (data.length === 0) {
    return (
      <Box data-testid={loadedTestId} sx={{ padding: tokens.spacing[4] }}>
        <Body>{emptyMessage}</Body>
      </Box>
    );
  }

  return (
    <div data-testid={loadedTestId} style={{ display: "contents" }}>
      {children(data)}
    </div>
  );
}
